import React, { useEffect, useState } from 'react';

import { Stack, Title, Paper, Text, Button, LoadingOverlay } from '@mantine/core';
import { useAuth } from 'context/auth-context';
import useAccountLink from 'hooks/stripe/useAccountLink';
import useStripeConnectAccount from 'hooks/stripe/useStripeConnectAccount';

type OnboardingPageStates =
  | 'staff'
  | 'loading'
  | 'error'
  | 'not started'
  | 'pending'
  | 'verification failed';

const OnboardingPage = (): JSX.Element => {
  const { session } = useAuth();
  const [state, setState] = useState<OnboardingPageStates>('loading');

  const { data, isLoading, isSuccess, isError, isRefetching, refetch } = useStripeConnectAccount();
  const { data: accountLinkData } = useAccountLink({
    type: 'account_onboarding',
  });

  // Disable on the absence of a URL rather than on isLoading: a failed request leaves
  // isLoading false with no URL, and assigning '' to location.href silently reloads the page
  // instead of opening Stripe.
  const onboardingUrl = accountLinkData?.data.url;

  const handleRefetchClick = (): void => {
    refetch();
  };

  useEffect(() => {
    if (session?.role === 'staff') {
      setState('staff');
    } else {
      if (isLoading) {
        setState('loading');
      }
      if (isError) {
        setState('error');
      }
      if (isSuccess && data?.data) {
        if (data.data.detailsSubmitted) {
          if (data.data.hasPendingVerification) {
            setState('pending');
          } else {
            setState('verification failed');
          }
        } else {
          setState('not started');
        }
      }
    }
  }, [data?.data, isSuccess, session, isLoading, isError]);

  return (
    <Paper p={100} shadow='md' radius='md' sx={{ height: '100%' }}>
      <LoadingOverlay visible={state === 'loading'} />
      <Stack align='center' spacing='xl'>
        {state === 'staff' && (
          <Text size='lg' mt='xl'>
            You will have access to Kilo once the onboarding process has been completed.
          </Text>
        )}
        {state === 'error' && <Title order={2}>Oops! Something is wrong on our end.</Title>}
        {state === 'not started' && (
          <>
            <Title order={2}>Welcome! First things first...</Title>
            <Text size='lg'>Let&apos;s get you set up with Stripe so you can start accepting payments.</Text>
            <Button
              mt='xl'
              size='lg'
              disabled={!onboardingUrl}
              onClick={(): void => {
                if (!onboardingUrl) return;
                window.location.href = onboardingUrl;
              }}
            >
              Connect to Stripe
            </Button>
          </>
        )}
        {state === 'pending' && (
          <>
            <Title order={2}>Verification</Title>
            <Text size='lg'>Stripe is verifying the information submitted.</Text>
            <Text>This process can take from a few seconds to a few hours.</Text>
            <Button mt='xl' size='lg' loading={isRefetching} onClick={handleRefetchClick}>
              Refresh
            </Button>
          </>
        )}
        {state === 'verification failed' && (
          <>
            <Title order={2}>Verification Failed</Title>
            <Text size='lg'>It looks like something isn&apos;t quite right.</Text>
            <Text size='lg'>Please click the button to resolve verification issues.</Text>
            <Button
              mt='xl'
              size='lg'
              disabled={!onboardingUrl}
              onClick={(): void => {
                if (!onboardingUrl) return;
                window.location.href = onboardingUrl;
              }}
            >
              Connect to Stripe
            </Button>
          </>
        )}
      </Stack>
    </Paper>
  );
};

export default OnboardingPage;
